/* Bandsintown's artist app ID is a public identifier used by its event links. */
const dateFormatter = new Intl.DateTimeFormat('en-GB', {
  day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC'
});

function safeURL(value) {
  try {
    const url = new URL(value);
    return url.protocol === 'https:' && !url.username && !url.password ? url.href : '';
  } catch { return ''; }
}

export function normalizeEvents(payload, artistId, limit = 6) {
  if (!Array.isArray(payload)) throw new Error('Invalid events response');
  const seen = new Set();
  const shows = payload.flatMap(event => {
    if (!event || typeof event !== 'object') return [];
    if (event.artist_id != null && String(event.artist_id) !== String(artistId)) return [];
    const date = typeof event.datetime === 'string' ? event.datetime.slice(0, 10) : '';
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return [];
    const localDay = new Date(`${date}T12:00:00Z`);
    if (!Number.isFinite(localDay.getTime()) || localDay.toISOString().slice(0, 10) !== date) return [];
    const id = String(event.id || `${date}:${event.venue?.name || ''}`);
    if (seen.has(id)) return [];
    seen.add(id);
    const eventURL = safeURL(event.url);
    const offers = Array.isArray(event.offers) ? event.offers : [];
    const ticket = offers.find(offer => offer && /ticket|presale/i.test(offer.type || '') && offer.status === 'available' && safeURL(offer.url));
    // Bandsintown can return type "Sold Out" with status "available".
    const soldOut = !ticket && offers.some(offer => offer && /sold[\s_-]*out/i.test(`${offer.type || ''} ${offer.status || ''}`));
    const venue = event.venue || {};
    return [{
      id, date, dateLabel: dateFormatter.format(localDay).toUpperCase(),
      venue: typeof venue.name === 'string' && venue.name.trim() ? venue.name : 'Venue TBA',
      location: typeof venue.location === 'string' && venue.location.trim()
        ? venue.location : [venue.city, venue.region, venue.country].filter(value => typeof value === 'string' && value.trim()).join(', '),
      eventURL, ticketURL: ticket ? safeURL(ticket.url) : '', soldOut
    }];
  }).sort((a, b) => a.date.localeCompare(b.date));
  if (payload.length && !shows.length) throw new Error('No valid artist events');
  return shows.slice(0, Math.max(1, Math.min(12, Number(limit) || 6)));
}

const requests = new Map();
async function fetchEvents(artistId, appId) {
  if (!/^\d+$/.test(artistId) || !appId.trim()) throw new Error('Missing artist settings');
  const url = new URL(`https://rest.bandsintown.com/artists/id_${artistId}/events/`);
  url.searchParams.set('app_id', appId.trim());
  url.searchParams.set('date', 'upcoming');
  const key = url.href;
  const cached = requests.get(key);
  if (cached && cached.expires > Date.now()) return cached.promise;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 10000);
  const promise = (async () => {
    try {
      const response = await fetch(url, {
        signal: controller.signal, credentials: 'omit', referrerPolicy: 'no-referrer',
        headers: { Accept: 'application/json' }
      });
      if (!response.ok) throw new Error('Events unavailable');
      const payload = await response.json();
      if (!Array.isArray(payload)) throw new Error('Invalid events response');
      return payload;
    } catch (error) {
      requests.delete(key);
      throw error;
    } finally { clearTimeout(timer); }
  })();
  requests.set(key, { promise, expires: Date.now() + 5 * 60 * 1000 });
  return promise;
}

function element(tag, className, text) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text) node.textContent = text;
  return node;
}

function link(text, href, className) {
  const node = element('a', className, text);
  node.href = href;
  node.target = '_blank';
  node.rel = 'noopener noreferrer';
  return node;
}

function showRow(show, labels) {
  const row = element('div', 'tour-date');
  row.setAttribute('role', 'listitem');
  const date = element('time', 'tour-date-label', show.dateLabel);
  date.dateTime = show.date;
  const details = element('div');
  const venue = element('strong');
  if (show.eventURL) venue.append(link(show.venue, show.eventURL, 'tour-venue-link'));
  else venue.textContent = show.venue;
  details.append(venue, element('span', '', show.location));
  row.append(date, details);
  if (show.soldOut) row.append(element('span', 'tour-sold-out', labels.soldOut));
  else if (show.ticketURL) row.append(link(labels.tickets, show.ticketURL, 'text-link tour-ticket-link'));
  else if (show.eventURL) row.append(link(labels.details, show.eventURL, 'text-link tour-ticket-link'));
  return row;
}

const initialized = new WeakSet();
export async function loadShows(container) {
  if (initialized.has(container)) return;
  initialized.add(container);
  const status = container.querySelector('[data-live-status]');
  const list = container.querySelector('[data-live-events]');
  container.setAttribute('aria-busy', 'true');
  status.textContent = container.dataset.loadingMessage;
  try {
    const payload = await fetchEvents(container.dataset.artistId, container.dataset.appId);
    const shows = normalizeEvents(payload, container.dataset.artistId, container.dataset.limit);
    if (!container.isConnected) return;
    list.replaceChildren(...shows.map(show => showRow(show, {
      tickets: container.dataset.ticketsLabel,
      details: container.dataset.detailsLabel,
      soldOut: container.dataset.soldOutLabel
    })));
    list.hidden = shows.length === 0;
    status.textContent = shows.length ? '' : container.dataset.emptyMessage;
    status.hidden = shows.length > 0;
  } catch {
    if (!container.isConnected) return;
    list.replaceChildren();
    list.hidden = true;
    status.hidden = false;
    status.textContent = container.dataset.errorMessage;
  } finally { container.setAttribute('aria-busy', 'false'); }
}

if (typeof document !== 'undefined') {
  const init = (root = document) => root.querySelectorAll('[data-bandsintown]').forEach(loadShows);
  init();
  document.addEventListener('shopify:section:load', event => init(event.target));
}
